// business.service.ts
import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable } from 'rxjs'
import { Business } from '../store/business/business.state'
import { eps } from './url.service'

@Injectable({
  providedIn: 'root'
})
export class BusinessService {
  private apiUrl = eps.company.businessApi

  constructor(private http: HttpClient) {}

  // getBusinesses(): Observable<any> {
  //   return this.http.get<any>(this.apiUrl)
  // }

  getBusinesses(params?: string | null, page?: number, limit?: number): Observable<any> {
    const url = params
      ? `${this.apiUrl}?${params}&page=${page}&limit=${limit}`
      : `${this.apiUrl}?page=${page}&limit=${limit}`
    return this.http.get<any>(url)
  }

  blockBusiness(businessId: string, status: boolean): Observable<any> {
    return this.http.put(`${this.apiUrl}/block/${businessId}`, { disabled: status })
  }

  unblockBusiness(businessId: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/unblock/${businessId}`, {})
  }

  deleteBusiness(businessId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${businessId}`, {})
  }

  createBusiness(business: {
    businessName: string
    picture: string | null
    website: string
    userId: string
    parentBusinessId: string | null
    planId?: string | null
    ownerFirstName?: string | null
    ownerLastName?: string | null
    ownerEmail?: string | null
    ownerMobile?: string | null
  }): Observable<Business> {
    const formData = new FormData()
    formData.append('businessName', business.businessName)
    formData.append('website', business.website)
    formData.append('userId', business.userId)
    if (business.parentBusinessId) {
      formData.append('parentBusinessId', business.parentBusinessId)
    }
    // If there is a picture, append it as well
    if (business.picture) {
      formData.append('picture', business.picture)
    }
    if (business.planId) {
      formData.append('planId', business.planId)
    }

    if (business.ownerFirstName) {
      formData.append('ownerFirstName', business.ownerFirstName)
    }

    if (business.ownerLastName) {
      formData.append('ownerLastName', business.ownerLastName)
    }

    if (business.ownerEmail) {
      formData.append('ownerEmail', business.ownerEmail)
    }

    if (business.ownerMobile) {
      formData.append('ownerMobile', business.ownerMobile)
    }

    return this.http.post<Business>(`${this.apiUrl}`, formData)
  }

  // Fetch business by ID
  getBusinessById(id: string): Observable<Business> {
    return this.http.get<Business>(`${this.apiUrl}/${id}`, {
      params: { businessId: id }
    })
  }

  updateBusiness(business: {
    id: string
    businessName: string
    picture: string | null
    website: string
    address: string
    cityId: number
    zipCode: string
    referenceIdFacebook: string
    referenceIdInstagram: string
    referenceIdLinkedIn: string
    referenceIdTwitter: string
    referenceIdWhatsapp: string
    empidprefix: string | null | undefined
    empidprefixStart: string | null | undefined
    contractoridprefix: string | null | undefined
    contractoridprefixStart: string | null | undefined
    traineeidprefix: string | null | undefined
    traineeidprefixStart: string | null | undefined
    currencyId: number | null | undefined
    incorporationNo: string | null | undefined
    incorporationDate: Date | null | undefined
    PAN: string | null | undefined
    GST: string | null | undefined
    userId: string | null | undefined
    longitude?: number | null | undefined
    latitude?: number | null | undefined
    isAutoClockInOut: boolean | null | undefined
    isMobileApp: boolean | null | undefined
  }): Observable<Business> {
    const formData = new FormData()
    formData.append('businessName', business.businessName)
    formData.append('website', business.website)
    if (business.empidprefix !== null && business.empidprefix !== undefined) {
      formData.append('empidprefix', business.empidprefix)
    }

    if (business.empidprefixStart !== null && business.empidprefixStart !== undefined) {
      formData.append('empidprefixStart', business.empidprefixStart.toString())
    }

    if (business.contractoridprefix !== null && business.contractoridprefix !== undefined) {
      formData.append('contractoridprefix', business.contractoridprefix)
    }

    if (
      business.contractoridprefixStart !== null &&
      business.contractoridprefixStart !== undefined
    ) {
      formData.append('contractoridprefixStart', business.contractoridprefixStart.toString())
    }

    if (business.traineeidprefix !== null && business.traineeidprefix !== undefined) {
      formData.append('traineeidprefix', business.traineeidprefix)
    }

    if (business.traineeidprefixStart !== null && business.traineeidprefixStart !== undefined) {
      formData.append('traineeidprefixStart', business.traineeidprefixStart.toString())
    }
    // Append the picture if available
    if (business.picture) {
      formData.append('picture', business.picture)
    }

    if (business.incorporationNo !== null && business.incorporationNo !== undefined) {
      formData.append('incorporationNo', business.incorporationNo.toString())
    }

    if (business.incorporationDate !== null && business.incorporationDate !== undefined) {
      formData.append('incorporationDate', business.incorporationDate.toString())
    }

    if (business.PAN !== null && business.PAN !== undefined) {
      formData.append('PAN', business.PAN.toString())
    }

    if (business.GST !== null && business.GST !== undefined) {
      formData.append('GST', business.GST.toString())
    }

    // Include other necessary fields as FormData entries
    if (business.address) formData.append('address', business.address)
    if (business.cityId) formData.append('cityId', `${business.cityId}`) // or other appropriate format
    if (business.zipCode) formData.append('zipCode', business.zipCode)
    // ✅ SAFE: Explicit null/undefined checks + .toString()
    if (business.longitude !== null && business.longitude !== undefined) {
      formData.append('longitude', business.longitude.toString())
    }

    if (business.latitude !== null && business.latitude !== undefined) {
      formData.append('latitude', business.latitude.toString())
    }

    if (business.isAutoClockInOut !== null && business.isAutoClockInOut !== undefined) {
      formData.append('isAutoClockInOut', business.isAutoClockInOut.toString())
    }

    if (business.isMobileApp !== null && business.isMobileApp !== undefined) {
      formData.append('isMobileApp', business.isMobileApp.toString())
    }
    if (business.referenceIdFacebook !== undefined) {
      formData.append('referenceIdFacebook', business.referenceIdFacebook ?? '')
    }
    if (business.referenceIdInstagram !== undefined) {
      formData.append('referenceIdInstagram', business.referenceIdInstagram ?? '')
    }
    if (business.referenceIdLinkedIn !== undefined) {
      formData.append('referenceIdLinkedIn', business.referenceIdLinkedIn ?? '')
    }
    if (business.referenceIdTwitter !== undefined) {
      formData.append('referenceIdTwitter', business.referenceIdTwitter ?? '')
    }
    if (business.referenceIdWhatsapp !== undefined) {
      formData.append('referenceIdWhatsapp', business.referenceIdWhatsapp ?? '')
    }
    if (business.currencyId) formData.append('currencyId', `${business.currencyId}`)
    if (business.userId) formData.append('userId', `${business.userId}`)

    return this.http.put<Business>(`${this.apiUrl}/${business.id}`, formData)
  }

  assignOwner(business: { id: string; userId: string }): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/assignOwner/${business.id}`, business)
  }

  getAllBusinesses(disabled?: boolean, isParent?: boolean): Observable<any> {
    let params = new HttpParams()

    if (disabled !== undefined) {
      params = params.set('disabled', String(disabled))
    }

    if (isParent !== undefined) {
      params = params.set('isParent', String(isParent))
    }

    return this.http.get<any>(`${this.apiUrl}/all`, { params })
  }

  getMultiBusinesses(): Observable<Business[]> {
    return this.http.get<Business[]>(`${this.apiUrl}/multibusiness`)
  }
}
